import type * as Communicator from '@hoops/web-viewer'
import { type MutableRefObject } from 'react'

interface HWVNode {
  id: number
  name: string
  properties: object | null
  children: HWVNode[]
}

/**
 * Returns the IDs for all leaf nodes of a model tree.
 * @param model The model that has been loaded into the Hoops WebViewer.
 * @returns List of leaf node IDs.
 */
export function getLeafNodeIds(hwv: MutableRefObject<Communicator.WebViewer | null>): number[] {
  const model: Communicator.core.IModel = hwv.current!.model
  const leafNodeIds: number[] = []
  const rootId = model.getAbsoluteRootNode()
  const collectLeafs = (nodeId: number) => {
    const children = model.getNodeChildren(nodeId)
    if (Array.isArray(children) && children.length > 0) {
      children.forEach(nodeId => collectLeafs(nodeId))
    } else {
      // there are no children -> this must be a leaf node
      leafNodeIds.push(nodeId)
    }
  }
  // start traversing the model tree
  collectLeafs(rootId)
  return leafNodeIds
}

/**
 * Returns the IDs for all NON-leaf (internal) nodes of a model tree, including the root.
 * @param hwv WebViewer ref
 * @returns List of non-leaf node IDs
 */
export function getNonLeafNodeIds(hwv: MutableRefObject<Communicator.WebViewer | null>): number[] {
  const model: Communicator.core.IModel = hwv.current!.model
  const nonLeafNodeIds: number[] = []
  const rootId = model.getAbsoluteRootNode()
  const collectNonLeafs = (nodeId: number) => {
    const children = model.getNodeChildren(nodeId)
    if (Array.isArray(children) && children.length > 0) {
      // node has children -> non-leaf
      nonLeafNodeIds.push(nodeId)
      children.forEach(id => collectNonLeafs(id))
    }
  }
  collectNonLeafs(rootId)
  return nonLeafNodeIds
}

/**
 * Returns a leaf node id for a given node id. If the provided id is already a leaf,
 * it is returned. Otherwise, a descendant leaf is searched and returned. If none is found,
 * null is returned.
 */
export function findLeafForNode(
  leafNodesDatasource: MutableRefObject<number[]>,
  hwv: MutableRefObject<Communicator.WebViewer | null>,
  nodeId: number
): number | null {
  if (leafNodesDatasource.current.includes(nodeId)) return nodeId
  const model = hwv.current?.model
  if (!model) return null

  const stack: number[] = [nodeId]
  while (stack.length > 0) {
    const current = stack.pop() as number
    const children = model.getNodeChildren(current, false)
    if (Array.isArray(children) && children.length > 0) {
      for (const child of children) {
        if (leafNodesDatasource.current.includes(child)) return child
        stack.push(child)
      }
    }
  }
  return null
}

/**
 * Returns all descendant leaf node ids for a given node id.
 */
export function getDescendantLeafIds(
  leafNodesDatasource: MutableRefObject<number[]>,
  hwv: MutableRefObject<Communicator.WebViewer | null>,
  nodeId: number
): number[] {
  const model = hwv.current?.model
  if (!model) return []
  const result: number[] = []
  const stack: number[] = [nodeId]
  while (stack.length > 0) {
    const current = stack.pop() as number
    const children = model.getNodeChildren(current, false)
    if (Array.isArray(children) && children.length > 0) {
      for (const child of children) {
        if (leafNodesDatasource.current.includes(child)) result.push(child)
        else stack.push(child)
      }
    }
  }
  return result
}

/**
 * Normalize a part display name by removing instance counters and parenthesis.
 * Examples:
 *  - "CARBURETOR(CARBURETOR.1)" -> "CARBURETOR"
 *  - "SCREW BACK(SCREW BACK.3)" -> "SCREW BACK"
 *  - "PA.07.00:1" -> "PA.07.00"
 *  - "PA.07.00:4" -> "PA.07.00"
 *  - "DIN 933 - replaced by DIN EN 24017 M6 x 28:1" -> "DIN 933 - replaced by DIN EN 24017 M6 x 28"
 */
export function toCanonicalPartName(name: string): string {
  let result = name.trim()

  // Remove parenthesis suffix like "(CARBURETOR.1)"
  const parenIdx = result.indexOf('(')
  if (parenIdx > 0) {
    result = result.slice(0, parenIdx).trim()
  }

  // Remove instance suffix like ":1", ":2", ":10" at the end
  const instanceSuffixMatch = result.match(/:(\d+)$/)
  if (instanceSuffixMatch) {
    result = result.slice(0, -instanceSuffixMatch[0].length).trim()
  }

  return result
}

export async function getModelTree(hwv: MutableRefObject<Communicator.WebViewer | null>) {
  const rootNodeId = hwv.current!.model.getAbsoluteRootNode()
  const root: HWVNode = {
    id: rootNodeId,
    name: hwv.current!.model.getNodeName(rootNodeId) as string,
    properties: await hwv.current!.model.getNodeProperties(rootNodeId),
    children: [],
  }
  const getSubTree = async (nodeId: number): Promise<HWVNode[]> => {
    const children = hwv.current?.model.getNodeChildren(nodeId, false)
    if (children) {
      return Promise.all(
        children.map(async (childId: number) => {
          return {
            id: childId,
            name: hwv.current!.model.getNodeName(childId) as string,
            properties: await hwv.current!.model.getNodeProperties(childId),
            children: await getSubTree(childId),
          }
        })
      )
    } else return []
  }

  return {
    ...root,
    children: await getSubTree(rootNodeId),
  }
}
